/**
 * BestandsanalysePanel — Risiko-Karten-Grid + Indikator-Detailansicht.
 *
 * Statische Dummy-Daten, Backend-Anbindung folgt später.
 * Design: 2-Spalten-Grid, Klick öffnet Detail inline unterhalb.
 */

import { useState, useCallback } from 'react'

// ── Typen ────────────────────────────────────────────────────

type RiskLevel = 'gering' | 'mittel' | 'hoch'

type IndicatorStatus = 'vollständig' | 'teilweise' | 'nicht nötig' | 'bewertet' | 'ausstehend'

interface Indicator {
  name: string
  status: IndicatorStatus
  note: string
}

interface Category {
  id: string
  title: string
  percent: number        // 0-100: Erfassungsgrad
  risk: RiskLevel
  summary: string        // Freitext oben im Detail-Panel
  indicators: Indicator[]
}

// ── Farben ───────────────────────────────────────────────────

const RISK_COLORS: Record<RiskLevel, string> = {
  gering: '#3d8c40',
  mittel: '#c47f17',
  hoch:   '#c0392b',
}

const RISK_LABELS: Record<RiskLevel, string> = {
  gering: 'Gering',
  mittel: 'Mittel',
  hoch:   'Hoch',
}

const RISK_BG: Record<RiskLevel, string> = {
  gering: '#edf7ed',
  mittel: '#fef7ec',
  hoch:   '#fdeceb',
}

const STATUS_COLORS: Record<IndicatorStatus, string> = {
  'vollständig': '#3d8c40',
  'teilweise':   '#c47f17',
  'nicht nötig': '#3d8c40',
  'bewertet':    '#3d8c40',
  'ausstehend':  '#999',
}

// ── Dummy-Daten ──────────────────────────────────────────────

const CATEGORIES: Category[] = [
  {
    id: 'tragwerk',
    title: 'Tragwerk',
    percent: 92,
    risk: 'gering',
    summary: 'Gut dokumentiert, geprüfte Statik 1976. Lastreserven bekannt, Aufstockung in 3 Varianten bewertet.',
    indicators: [
      { name: 'Bestandsdokumentation', status: 'vollständig', note: 'Geprüfte Statik 1976' },
      { name: 'Gründungsanalyse', status: 'vollständig', note: 'Auslastung 39–62 %' },
      { name: 'Wandauslastung', status: 'vollständig', note: 'η max 57 % (KSL)' },
      { name: 'Stützen & Unterzüge', status: 'teilweise', note: 'Einzelfall nötig' },
      { name: 'Materialbeprobung', status: 'nicht nötig', note: 'Doku ersetzt Probe' },
      { name: 'Lastreserve Aufstockung', status: 'bewertet', note: '3 Varianten A/B/C' },
    ],
  },
  {
    id: 'schadstoffe',
    title: 'Schadstoffe',
    percent: 68,
    risk: 'hoch',
    summary: 'Asbest in Fassadenplatten nachgewiesen. KMF-Verdacht in Zwischendecken. Sanierungskonzept erforderlich.',
    indicators: [
      { name: 'Asbest-Screening', status: 'vollständig', note: 'Fassade positiv' },
      { name: 'KMF-Untersuchung', status: 'teilweise', note: 'Decke OG ausstehend' },
      { name: 'PCB-Analyse', status: 'vollständig', note: 'Nicht nachgewiesen' },
      { name: 'PAK-Bewertung', status: 'vollständig', note: 'Parkettkleber unauff.' },
      { name: 'Sanierungskonzept', status: 'ausstehend', note: 'In Bearbeitung' },
    ],
  },
  {
    id: 'zirkularitaet',
    title: 'Zirkularität',
    percent: 80,
    risk: 'mittel',
    summary: 'Materialpass für Rohbau erstellt. Rückbaukonzept in Grundzügen vorhanden, Detailbewertung Innenausbau fehlt.',
    indicators: [
      { name: 'Materialpass Rohbau', status: 'vollständig', note: 'Madaster-Export' },
      { name: 'Materialpass Ausbau', status: 'teilweise', note: 'EG + OG1 erfasst' },
      { name: 'Rückbaukonzept', status: 'teilweise', note: 'Grundzüge vorhanden' },
      { name: 'Wiederverwendungspot.', status: 'bewertet', note: '38 % Masse' },
    ],
  },
  {
    id: 'oekobilanz',
    title: 'Ökobilanz',
    percent: 85,
    risk: 'mittel',
    summary: 'LCA-Berechnung nach QNG durchgeführt. Graue Energie im Bestand dokumentiert, Einsparpotenzial bei Sanierung bewertet.',
    indicators: [
      { name: 'LCA Bestandsgebäude', status: 'vollständig', note: 'QNG-konform' },
      { name: 'Graue Energie', status: 'vollständig', note: '820 kWh/m² BGF' },
      { name: 'CO₂-Bilanz Betrieb', status: 'vollständig', note: '42 kg/m²a' },
      { name: 'Sanierungsszenario', status: 'bewertet', note: '–35 % möglich' },
      { name: 'Referenzgebäude-Vgl.', status: 'teilweise', note: 'Benchmark fehlt' },
    ],
  },
  {
    id: 'digitalisierung',
    title: 'Digitalisierung',
    percent: 95,
    risk: 'gering',
    summary: 'Punktwolke vollständig, BIM-Modell LOD 300 abgeleitet. Raumbuch digital erfasst.',
    indicators: [
      { name: 'Punktwolke', status: 'vollständig', note: '3D-Scan komplett' },
      { name: 'BIM-Modell', status: 'vollständig', note: 'LOD 300 / IFC' },
      { name: 'Raumbuch digital', status: 'vollständig', note: 'orto.space' },
      { name: 'Plandigitalisierung', status: 'vollständig', note: '42 Pläne georef.' },
      { name: 'Fotodokumentation', status: 'vollständig', note: '1.240 Fotos' },
    ],
  },
  {
    id: 'ertuechtigung',
    title: 'Ertüchtigung',
    percent: 72,
    risk: 'mittel',
    summary: 'Energiekonzept liegt vor. Brandschutznachweis in Überarbeitung, Barrierefreiheit teilweise geprüft.',
    indicators: [
      { name: 'Energiekonzept', status: 'vollständig', note: 'KfW 70 EE möglich' },
      { name: 'Brandschutznachweis', status: 'teilweise', note: 'Fluchtweg OG offen' },
      { name: 'Barrierefreiheit', status: 'teilweise', note: 'Aufzug fehlt' },
      { name: 'Schallschutz', status: 'ausstehend', note: 'Noch nicht geprüft' },
      { name: 'Haustechnik-Bestand', status: 'vollständig', note: 'Bj. 1998, tlw. erneut' },
    ],
  },
  {
    id: 'kostenrisiken',
    title: 'Kostenrisiken',
    percent: 55,
    risk: 'hoch',
    summary: 'Grobrisikoabschätzung zeigt erhöhte Unsicherheit bei Schadstoffsanierung und Tragwerksertüchtigung.',
    indicators: [
      { name: 'Kostenschätzung', status: 'vollständig', note: 'DIN 276, KG 300–400' },
      { name: 'Risikoregister', status: 'teilweise', note: '12 Risiken erfasst' },
      { name: 'Schadstoff-Kosten', status: 'teilweise', note: 'Spanne 180–340 T€' },
      { name: 'Fördermittel-Check', status: 'ausstehend', note: 'BEG/KfW prüfen' },
      { name: 'Zeitrisiko Genehmigung', status: 'bewertet', note: '+4 Monate möglich' },
    ],
  },
]

// ── Legende ──────────────────────────────────────────────────

const LEGEND: { color: string; label: string }[] = [
  { color: '#3d8c40', label: 'vollständig / kein Risiko' },
  { color: '#c47f17', label: 'teilweise / Hinweis' },
  { color: '#c0392b', label: 'Risiko identifiziert' },
  { color: '#999',    label: 'Stichprobe / begrenzt' },
]

// ── Komponente ───────────────────────────────────────────────

export function BestandsanalysePanel() {
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const selected = CATEGORIES.find(c => c.id === selectedId) ?? null

  const handleCardClick = useCallback((id: string) => {
    setSelectedId(prev => (prev === id ? null : id))
  }, [])

  return (
    <div className="ba-panel">
      {/* Karten-Grid */}
      <div className="ba-grid">
        {CATEGORIES.map(cat => {
          const isSelected = selectedId === cat.id
          return (
            <button
              key={cat.id}
              className={`ba-card${isSelected ? ' ba-card--selected' : ''}`}
              style={isSelected ? { borderColor: RISK_COLORS[cat.risk] } as React.CSSProperties : undefined}
              onClick={() => handleCardClick(cat.id)}
            >
              <span className="ba-card__title">{cat.title}</span>
              <div className="ba-card__bar">
                <div
                  className="ba-card__bar-fill"
                  style={{
                    width: `${cat.percent}%`,
                    background: RISK_COLORS[cat.risk],
                  }}
                />
              </div>
              <div className="ba-card__footer">
                <span className="ba-card__pct">{cat.percent}% erfasst</span>
                <span
                  className="ba-card__badge"
                  style={{ color: RISK_COLORS[cat.risk] }}
                >
                  {RISK_LABELS[cat.risk]}
                </span>
              </div>
            </button>
          )
        })}
      </div>

      {/* Detail-Panel (inline) */}
      {selected && (
        <div className="ba-detail">
          <div className="ba-detail__header">
            <span className="ba-detail__title">{selected.title} – Indikatoren</span>
          </div>
          <p className="ba-detail__summary">{selected.summary}</p>

          <div className="ba-detail__table">
            {selected.indicators.map((ind, i) => (
              <div key={i} className="ba-detail__row">
                <span
                  className="ba-detail__dot"
                  style={{ background: STATUS_COLORS[ind.status] }}
                />
                <span className="ba-detail__name">{ind.name}</span>
                <span className="ba-detail__status">{ind.status}</span>
                <span className="ba-detail__note">{ind.note}</span>
              </div>
            ))}
          </div>

          {/* Legende */}
          <div className="ba-legend">
            {LEGEND.map((l, i) => (
              <span key={i} className="ba-legend__item">
                <span className="ba-legend__dot" style={{ background: l.color }} />
                {l.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
